import React from 'react';
import { Box, TextField, Chip, Typography } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import { CustomTooltip } from '../../keywordResearch/CustomTooltip';
import { TrackYourAIVisibilityCSS as css } from '../style';
import { orbitAiStepCSS } from '../OrbitAiStepCSS';

interface ChipInputFieldProps {
  /** Label displayed above the input. */
  label?: string;
  /** Tooltip text shown next to the label. */
  tooltipText?: string;
  /** Placeholder text for the input. When omitted, a default is computed. */
  placeholder?: string;
  /** Current text value of the input. */
  value: string;
  /** Whether the input is in an error state. */
  error?: boolean;
  /** Error message shown below the input. */
  helperText?: string;
  /** List of chips displayed inside the input. */
  chips: string[];
  /** Maximum number of chips allowed (used for default placeholder logic). */
  maxLimit?: number;
  /** When true, the input is read-only and chips cannot be deleted. */
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<any>) => void;
  onBlur: () => void;
  onDelete: (chip: string) => void;
}

/**
 * Renders a labelled text-field with chip tags inside it.
 * Used for competitor / topic entry in each OrbitAI feature row.
 */
export const ChipInputField: React.FC<ChipInputFieldProps> = ({
  label = 'Enter up to 3 main Competitors',
  tooltipText,
  placeholder,
  value,
  error,
  helperText,
  chips,
  maxLimit = 3,
  disabled = false,
  onChange,
  onKeyDown,
  onBlur,
  onDelete
}) => {
  const effectivePlaceholder =
    placeholder !== undefined
      ? placeholder
      : chips && chips.length < maxLimit
        ? 'Enter competitor names (comma-separated)'
        : '';

  return (
    <Box sx={orbitAiStepCSS?.featureRow}>
      <Box sx={orbitAiStepCSS?.featureBox}>
        <Box sx={css.labelRow}>
          <Typography sx={css.label}>
            {label}
          </Typography>
          {tooltipText && (
            <CustomTooltip
              title={
                <Box>
                  <Typography sx={orbitAiStepCSS?.featureDesc}>
                    {tooltipText}
                  </Typography>
                </Box>
              }
              placement="bottom"
              arrow
            >
              <HelpIcon sx={css.helpIcon} />
            </CustomTooltip>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '20px',
            alignItems: 'center'
          }}
        >
          <TextField
            variant="outlined"
            multiline
            placeholder={effectivePlaceholder}
            fullWidth
            value={value}
            error={disabled ? false : error}
            helperText={disabled ? '' : (error ? helperText : '')}
            onChange={disabled ? undefined : onChange}
            onKeyDown={disabled ? undefined : onKeyDown}
            onBlur={disabled ? undefined : onBlur}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <Box sx={orbitAiStepCSS?.inputBoxStart}>
                  {chips?.map((kw, index) => (
                    <Chip
                      key={index}
                      label={kw}
                      onDelete={disabled ? undefined : () => onDelete(kw)}
                      sx={orbitAiStepCSS?.inputChip}
                      deleteIcon={
                        disabled ? undefined : (
                          <CloseIcon
                            sx={{
                              color: 'var(--color-text-dark) !important',
                              fontSize: '18px !important'
                            }}
                          />
                        )
                      }
                    />
                  ))}
                </Box>
              )
            }}
            sx={orbitAiStepCSS?.textFieldInput}
          />
        </Box>
      </Box>
    </Box>
  );
};
